// The support bubble is disabled: index.html no longer loads it and the widget code below is commented out. Still runs for the Cello badge.
import { watch, onUnmounted } from 'vue'
import { useConfigStore } from '@/stores/ConfigStore'
// import { useUserStore } from '@/stores/UserStore'
import { useGlobalStore } from '@/stores/GlobalStore'
import { useRoute } from 'vue-router'
import { updateCelloPosition, setCelloHidden } from '@/hooks/useCello'

const HIDDEN_ROUTES = [
  'MobileScan', 'MobileScanSides', 'MobileScanUpload', 'MobileScanCompleted',
  'SignIn', 'SignUp', 'SignUpEmail', 'ResetPassword', 'SetPassword', 'EmailSent', 'LinkExpired',
]

// let _observer = null
let _bottom   = '18px'
let _right    = '18px'
let _hidden   = false
// let _booted   = false

/* Widget DOM: the host, its launcher, the shadow-root CSS override and the observer that re-applies it.
const getHost = () => document.getElementById('cdp-support-center')
const getBtn  = () => getHost()?.shadowRoot?.querySelector('#cdp-widget-root button')

const clickWhenReady = (attempts = 20) => {
  const btn = getBtn()
  if (btn) { btn.click(); return }
  if (attempts > 0) setTimeout(() => clickWhenReady(attempts - 1), 100)
}

// Inject a <style> directly into the shadow root — overrides Tailwind's bottom-5/right-5
// via CSS cascade before the browser paints, eliminating any position flash.
const injectShadowCss = () => {
  const host = getHost()
  if (!host?.shadowRoot) return

  let style = host.shadowRoot.getElementById('cdp-pos-override')
  if (!style) {
    style = document.createElement('style')
    style.id = 'cdp-pos-override'
    host.shadowRoot.insertBefore(style, host.shadowRoot.firstChild)
  }

  if (_hidden) {
    // :host targets the shadow host from inside — the widget cannot override
    // its own injected shadow stylesheet, so this reliably wins over any
    // inline style the widget sets on the host element.
    style.textContent = `:host { display: none !important; }`
  } else {
    // Panel sits 76px above the button (bottom-24 vs bottom-5 = 96px - 20px gap).
    // Both elements use fixed positioning with hardcoded Tailwind right-5 classes —
    // we override both so the panel stays aligned with the button when repositioned.
    const panelBottom = `${parseInt(_bottom) + 76}px`
    style.textContent = `
      :host { display: block !important; }
      button[class*="z-[9999]"] { bottom: ${_bottom} !important; right: ${_right} !important; }
      div[class*="z-[9998]"]    { bottom: ${panelBottom} !important; right: ${_right} !important; background-color: #ffffff !important; }
    `
  }
}

const applyStyle = () => {
  const host = getHost()
  if (!host) return
  injectShadowCss()
}

// Observe only direct childList changes on the shadow root — fires if the widget
// completely recreates its DOM (e.g. re-init), NOT on every internal animation
// or state update. This avoids the tight-loop that subtree+attributes caused.
const startObserver = () => {
  const host = getHost()
  if (!host?.shadowRoot || _observer) return
  _observer = new MutationObserver(() => injectShadowCss())
  _observer.observe(host.shadowRoot, { childList: true })
}
*/

const setPosition = (bottom, right) => {
  _bottom = bottom
  _right  = right
  _hidden = false
  // applyStyle()
  setCelloHidden(false)
  updateCelloPosition(bottom, right)
}

const hide = () => {
  _hidden = true
  // applyStyle()
  setCelloHidden(true)
}

export const useCdpSupport = () => {
  const configStore = useConfigStore()
  // const userStore   = useUserStore()
  const globalStore = useGlobalStore()
  const route       = useRoute()

  const updatePosition = () => {
    const isMobile = window.innerWidth < 768
    const isMobileOrTablet = window.innerWidth < 1280

    // `fullScreen` routes (e.g. the pick-list print view) render without app
    // chrome — keep the floating support widget off them too. Also hide while a
    // date-range calendar is open so the bubble can't overlap it.
    if (
      HIDDEN_ROUTES.includes(route.name)
      || route.meta?.fullScreen
      || globalStore.datePickerOpen
      || globalStore.batchPreviewOpen
      // A portal panel tall enough to reach the corner (the Columns checklist
      // on a short viewport) sits UNDER these widgets — their shadow-DOM hosts
      // carry a z-index we don't control, so the panel can't win by stacking.
      || globalStore.cornerOverlayCount > 0
      || (isMobile && globalStore.sidebar)
      || isMobileOrTablet
    ) {
      hide()
      return
    }

    // When an AppDetailPanel is open the chat bubble would sit on top of the
    // panel's footer buttons (CDP-2281). Shift the widget left by the panel
    // width + a 12px gap so it clears the panel. The 8px floor avoids
    // pinning the bubble flush against the viewport edge on narrow screens.
    // A docked panel lies over the Help Hub column, so clear the wider of the two.
    const detailWidth = globalStore.detailPanelOpen ? globalStore.detailPanelWidth : 0
    const helpWidth = globalStore.helpHubOpen ? globalStore.helpHubWidth : 0
    const panelOffset = globalStore.detailPanelOpen || globalStore.helpHubOpen
      ? Math.max(Math.max(detailWidth, helpWidth) + 12, 8)
      : 0
    const withPanel = (right) => `${parseInt(right) + panelOffset}px`

    // AppPagination in fixed mode publishes its height here. With a bar on
    // screen the bubble sits directly ABOVE it with a small gap — measured from
    // the bar, not added to the page's own bottom offset, which used to stack
    // the two and float the bubble well clear of the bar.
    const BAR_GAP = 12
    const bar = globalStore.paginationBarHeight
    const withBar = (bottom) => (bar > 0 ? `${bar + BAR_GAP}px` : bottom)

    // ONE position for every page. Inventory / Batches / History used to add
    // their own 50–75px offsets to clear the pagination bar by hand; `withBar`
    // now does that from the bar's real height, so the hand-tuned numbers were
    // double-counting it and left the bubble sitting at a different spot on
    // every section. Both the support bubble and the Cello referral badge read
    // these values, so they move together.
    setPosition(
      withBar('18px'),
      withPanel(globalStore.videoWidgetOpen ? '380px' : '18px')
    )
  }

  /* Boot: hands the widget the dealer's identity once config lands, then positions and (for `?cdpSupport`) opens it.
  const boot = (token) => {
    if (_booted) return
    _booted = true

    const config = {
      apiUrl:       'https://support.carddealerpro.com/api',
      title:        'CDP Support',
      primaryColor: '#0086FF',
      guestMode:    false,
      pageContext:  {
        plan: configStore.config?.subscription?.plan_id ?? null,
      },
      user: {
        email:  userStore.user.email,
        userId: String(userStore.user.id),
        name:   [userStore.user.first_name, userStore.user.last_name].filter(Boolean).join(' '),
      },
    }
    
    // Logged-out: omit guestMode entirely so the widget shows its own sign-in/guest form

    const afterInit = () => {
      requestAnimationFrame(() => {
        startObserver()
        updatePosition()
        if (route.query.cdpSupport) {
          clickWhenReady()
        }
      })
    }

    if (window.CDPSupport) {
      window.CDPSupport.init(config)
      afterInit()
      return
    }

    const script = document.querySelector('script[src*="cdpsupport.replit.app"]')
    script?.addEventListener('load', () => {
      window.CDPSupport?.init(config)
      afterInit()
    }, { once: true })
  }
  */

  watch(() => configStore.config, (config) => {
    if (!config || !Object.keys(config).length) return
    // boot(config.support_token ?? null)
    // Of what booting did, only the badge's first position is still needed.
    updatePosition()
  }, { immediate: true })

  watch(route, (r) => {
    updatePosition()
    // if (r.query.cdpSupport) getHost()?.shadowRoot?.querySelector('#cdp-widget-root button')?.click()
  })
  watch(() => globalStore.videoWidgetOpen, updatePosition)
  watch(() => globalStore.sidebar, updatePosition)
  // Both flags shift the widget — they change together when a panel opens
  // but watching the width too covers the (unlikely) future case of a panel
  // whose width changes while it's already open.
  watch(() => globalStore.detailPanelOpen,  updatePosition)
  watch(() => globalStore.detailPanelWidth, updatePosition)
  watch(() => globalStore.helpHubOpen, updatePosition)
  watch(() => globalStore.helpHubWidth, updatePosition)
  watch(() => globalStore.paginationBarHeight, updatePosition)
  watch(() => globalStore.datePickerOpen, updatePosition)
  watch(() => globalStore.batchPreviewOpen, updatePosition)
  watch(() => globalStore.cornerOverlayCount, updatePosition)

  const onResize = () => updatePosition()
  window.addEventListener('resize', onResize)
  onUnmounted(() => window.removeEventListener('resize', onResize))

  /* Re-boot when auth state changes so identity is always current
  watch(() => userStore.user, (user, prev) => {
    if (!user) {
      // Logged out — re-init anonymously
      _booted = false
      boot(null)
    } else if (!prev) {
      // Just logged in — re-init with identity
      _booted = false
      boot(configStore.config.support_token ?? null)
    }
  })
  */
}
